import React, { useMemo, useState } from "react";
import { Empty, Input } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  TrendingUp,
  Clipboard,
  Book,
  FileText,
  ArrowRight,
  Star,
  Clock,
  Shield,
  Zap,
  List,
  Gift,
  BarChart2,
  AlertCircle,
  Search as SearchIcon,
} from "react-feather";

const { Search } = Input;

// ─── Report Catalogue ────────────────────────────────────────────────────────
// All POS reports are now defined directly inside the landing page.
// Adding a new report: add an entry to the correct category's reports[] array.
// Routes must match App.jsx/router config.
const posReports = [
  {
    category: "Sales Reports",
    color: "#2563eb",
    dotColor: "#2563eb",
    bgAccent: "rgba(37, 99, 235, 0.07)",
    description:
      "Day-level and date-range revenue summaries for daily reconciliation, outlet review, and management control.",
    reports: [
      {
        icon: <ShoppingCart size={20} />,
        title: "Daily Sales Report",
        description:
          "Bill-wise sales for a single business date with per-tax column breakdown. Covers subtotal, each tax type, discounts, delivery, and grand total.",
        link: "/pos/reports/daily-sales",
        badge: "Daily",
        signal: "Finance",
        route: "R1",
      },
      {
        icon: <TrendingUp size={20} />,
        title: "Date-wise Sales Summary",
        description:
          "Aggregated revenue, bill count, average bill value, and payment-mode breakdown across any date range.",
        link: "/pos/reports/datewise-sales",
        badge: "Range",
        signal: "Analytics",
        route: "R2",
      },
      {
        icon: <BarChart2 size={20} />,
        title: "Sales Analysis",
        description:
          "Multi-dimension view: order-type split, hourly heatmap, payment mix, top-10 items, and daily trend sparkline in one report.",
        link: "/pos/reports/sales-analysis",
        badge: "Insights",
        signal: "Management",
        route: "R8",
      },
    ],
  },
  {
    category: "Tax & Item Reports",
    color: "#0f766e",
    dotColor: "#14b8a6",
    bgAccent: "rgba(20, 184, 166, 0.07)",
    description:
      "Tax collection and item-level sales aggregation for finance sign-off, GST checks, and menu engineering.",
    reports: [
      {
        icon: <Book size={20} />,
        title: "Tax Collection Report",
        description:
          "Tax collected per tax type such as CGST, SGST, IGST, and other configured taxes across a date range.",
        link: "/pos/reports/tax-report",
        badge: "Tax",
        signal: "Finance",
        route: "R3",
      },
      {
        icon: <Clipboard size={20} />,
        title: "Item-wise Sales",
        description:
          "Qty sold, gross revenue, tax, and net revenue per menu item. Useful for best-seller, slow-mover, and category analysis.",
        link: "/pos/reports/item-sales",
        badge: "Item-wise",
        signal: "Operations",
        route: "R7",
      },
    ],
  },
  {
    category: "KOT & Complementary Reports",
    color: "#b45309",
    dotColor: "#f59e0b",
    bgAccent: "rgba(245, 158, 11, 0.07)",
    description:
      "Kitchen order tracking and complementary authorization audit for operational control and wastage visibility.",
    reports: [
      {
        icon: <AlertCircle size={20} />,
        title: "Pending KOTs",
        description:
          "Live KOT list with status filter, age in minutes, item count, and waiter assignment for kitchen control.",
        link: "/pos/reports/pending-kots",
        badge: "Live",
        signal: "Kitchen",
        route: "R4",
      },
      {
        icon: <Gift size={20} />,
        title: "Complementary Items",
        description:
          "Every item issued as complimentary from KOT and order sources, with qty, estimated cost, creator, and location.",
        link: "/pos/reports/complementary-items",
        badge: "Comp",
        signal: "Audit",
        route: "R5",
      },
      {
        icon: <List size={20} />,
        title: "Complementary Bills",
        description:
          "Bill-level list where is_complementary is true, including waived amount, item count, and staff creator.",
        link: "/pos/reports/complementary-bills",
        badge: "Comp",
        signal: "Audit",
        route: "R6",
      },
    ],
  },
];

const quickLinks = [
  {
    icon: <ShoppingCart size={16} />,
    title: "Daily Sales Report",
    link: "/pos/reports/daily-sales",
    helper: "Start with day-level revenue.",
  },
  {
    icon: <BarChart2 size={16} />,
    title: "Sales Analysis",
    link: "/pos/reports/sales-analysis",
    helper: "Hourly heatmap, payment mix, and top items.",
  },
  {
    icon: <Book size={16} />,
    title: "Tax Collection",
    link: "/pos/reports/tax-report",
    helper: "Review tax before finance handover.",
  },
  {
    icon: <AlertCircle size={16} />,
    title: "Pending KOTs",
    link: "/pos/reports/pending-kots",
    helper: "Track kitchen order status live.",
  },
  {
    icon: <Gift size={16} />,
    title: "Complementary Bills",
    link: "/pos/reports/complementary-bills",
    helper: "Authorization audit for comps.",
  },
];

const reportingTips = [
  {
    icon: <ShoppingCart size={15} />,
    title: "Start daily",
    body: "Use Daily Sales first, then drill into Item-wise Sales or Sales Analysis.",
  },
  {
    icon: <Book size={15} />,
    title: "Verify tax",
    body: "Run Tax Collection before finance handover or GST audit checks.",
  },
  {
    icon: <AlertCircle size={15} />,
    title: "Check KOTs",
    body: "Review Pending KOTs near day end to catch open kitchen orders.",
  },
  {
    icon: <Gift size={15} />,
    title: "Audit comps",
    body: "Review Complementary Bills weekly for authorization compliance.",
  },
];

const normalize = (value) => String(value || "").toLowerCase().trim();

const ReportLandingPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const q = useMemo(() => normalize(searchQuery), [searchQuery]);

  const filteredCategories = useMemo(() => {
    return posReports
      .map((category) => ({
        ...category,
        reports: category.reports.filter((report) => {
          if (!q) return true;

          return [
            report.title,
            report.description,
            report.badge,
            report.signal,
            report.route,
            category.category,
            category.description,
          ]
            .map(normalize)
            .some((text) => text.includes(q));
        }),
      }))
      .filter((category) => category.reports.length > 0);
  }, [q]);

  const filteredQuickLinks = useMemo(() => {
    if (!q) return quickLinks;

    return quickLinks.filter((link) =>
      [link.title, link.helper].map(normalize).some((text) => text.includes(q))
    );
  }, [q]);

  const totalReports = filteredCategories.reduce(
    (sum, category) => sum + category.reports.length,
    0
  );

  const totalAvailableReports = posReports.reduce(
    (sum, category) => sum + category.reports.length,
    0
  );

  const featuredReport =
    filteredCategories?.[0]?.reports?.[0] || posReports[0].reports[0];

  const handleCardClick = (link) => {
    navigate(link);
  };

  const handleSearch = (value) => {
    setSearchQuery(String(value || ""));
  };

  return (
    <div className="page-wrapper">
      <style>{`
        .ns-report-page {
          min-height: calc(100vh - 70px);
          background:
            radial-gradient(circle at 10% 4%, rgba(37, 99, 235, 0.10), transparent 28%),
            radial-gradient(circle at 86% 0%, rgba(20, 184, 166, 0.12), transparent 26%),
            #f8fafc;
          padding-bottom: 26px;
        }

        .ns-report-hero {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.18), transparent 34%),
            radial-gradient(circle at top right, rgba(34, 197, 94, 0.11), transparent 30%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 26px;
          box-shadow: 0 20px 52px rgba(15, 23, 42, 0.08);
          padding: 22px;
          margin-bottom: 18px;
        }

        .ns-report-hero::after {
          content: "";
          position: absolute;
          width: 240px;
          height: 240px;
          right: -90px;
          top: -110px;
          border-radius: 999px;
          background: rgba(37, 99, 235, 0.08);
          pointer-events: none;
        }

        .ns-report-hero-inner {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 18px;
          flex-wrap: wrap;
        }

        .ns-report-eyebrow {
          color: #2563eb;
          font-size: 0.72rem;
          font-weight: 950;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          margin-bottom: 7px;
        }

        .ns-report-title-row {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .ns-report-title-icon {
          width: 46px;
          height: 46px;
          border-radius: 17px;
          background: linear-gradient(135deg, #1d4ed8, #38bdf8);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.24);
        }

        .ns-report-title {
          color: #001f5b;
          font-size: 1.6rem;
          line-height: 1.14;
          font-weight: 950;
          margin: 0;
        }

        .ns-report-subtitle {
          color: #64748b;
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 10px 0 0;
          max-width: 800px;
        }

        .ns-report-search-wrap {
          min-width: 320px;
          width: min(440px, 100%);
        }

        .ns-report-search-card {
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
          padding: 12px;
        }

        .ns-report-search-card .ant-input-search .ant-input {
          height: 44px;
          border-radius: 14px 0 0 14px !important;
          font-weight: 750;
        }

        .ns-report-search-card .ant-input-search-button {
          height: 44px !important;
          border-radius: 0 14px 14px 0 !important;
          font-weight: 900;
        }

        .ns-report-search-help {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          align-items: center;
          color: #64748b;
          font-size: 0.74rem;
          font-weight: 800;
          margin-top: 9px;
          padding: 0 2px;
        }

        .ns-report-search-help strong {
          color: #0f172a;
        }

        .ns-report-hero-pills {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 16px;
        }

        .ns-report-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #334155;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
          padding: 7px 11px;
          font-size: 0.78rem;
          font-weight: 900;
          white-space: nowrap;
        }

        .ns-report-content-card {
          background: rgba(255, 255, 255, 0.96);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 26px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
          padding: 18px;
        }

        .ns-rs-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 320px;
          gap: 20px;
          align-items: start;
        }

        .ns-rs-section-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 14px;
          flex-wrap: wrap;
          margin-bottom: 18px;
        }

        .ns-rs-kicker {
          color: #2563eb;
          text-transform: uppercase;
          letter-spacing: 0.13em;
          font-size: 0.7rem;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-rs-heading {
          color: #001f5b;
          font-size: 1.1rem;
          font-weight: 950;
          margin: 0;
        }

        .ns-rs-help {
          color: #64748b;
          font-size: 0.84rem;
          line-height: 1.55;
          margin: 6px 0 0;
        }

        .ns-rs-result-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border-radius: 999px;
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          padding: 7px 11px;
          font-size: 0.75rem;
          font-weight: 950;
          white-space: nowrap;
        }

        .ns-rs-category-card {
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          padding: 18px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
          margin-bottom: 18px;
        }

        .ns-rs-category-title-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }

        .ns-rs-category-title {
          color: #031b4e;
          font-size: 1rem;
          font-weight: 950;
          margin: 0;
          display: inline-flex;
          align-items: center;
          gap: 9px;
        }

        .ns-rs-category-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          flex-shrink: 0;
        }

        .ns-rs-category-description {
          color: #64748b;
          font-size: 0.82rem;
          margin: 5px 0 0;
          line-height: 1.5;
        }

        .ns-rs-count {
          border-radius: 999px;
          background: #f0fdf4;
          color: #166534;
          border: 1px solid #bbf7d0;
          padding: 5px 10px;
          font-size: 0.7rem;
          font-weight: 950;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .ns-rs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 14px;
        }

        .ns-rs-card {
          border: 1px solid rgba(15, 23, 42, 0.09);
          border-radius: 18px;
          background: #ffffff;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
          transition: transform 0.17s ease, box-shadow 0.17s ease, border-color 0.17s ease;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .ns-rs-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.1);
          border-color: rgba(37, 99, 235, 0.24);
        }

        .ns-rs-card:focus-visible {
          outline: 3px solid rgba(37, 99, 235, 0.22);
          outline-offset: 3px;
        }

        .ns-rs-card-inner {
          padding: 16px;
          display: flex;
          flex-direction: column;
          height: 100%;
          gap: 12px;
        }

        .ns-rs-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .ns-rs-icon-box {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .ns-rs-badge {
          font-size: 0.68rem;
          font-weight: 950;
          border-radius: 999px;
          padding: 3px 9px;
          border: 1px solid;
          white-space: nowrap;
        }

        .ns-rs-route-tag {
          font-size: 0.64rem;
          font-weight: 800;
          color: #94a3b8;
          font-family: monospace;
        }

        .ns-rs-card-title {
          color: #0f172a;
          font-size: 0.92rem;
          font-weight: 950;
          margin: 0 0 5px;
          line-height: 1.25;
        }

        .ns-rs-card-description {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.55;
          margin: 0;
          flex: 1;
        }

        .ns-rs-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 12px;
          border-top: 1px solid rgba(15, 23, 42, 0.06);
        }

        .ns-rs-signal {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.73rem;
          font-weight: 950;
          color: #64748b;
        }

        .ns-rs-open {
          width: 28px;
          height: 28px;
          border-radius: 10px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s ease;
        }

        .ns-rs-card:hover .ns-rs-open {
          background: #1d4ed8;
          color: #ffffff;
        }

        .ns-rs-side {
          display: grid;
          gap: 16px;
          position: sticky;
          top: 80px;
        }

        .ns-rs-side-title {
          font-size: 0.88rem;
          font-weight: 950;
          color: #0f172a;
          margin: 0 0 12px;
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .ns-rs-featured {
          background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%);
          border-radius: 20px;
          padding: 16px;
          box-shadow: 0 12px 32px rgba(29, 78, 216, 0.22);
        }

        .ns-rs-featured .ns-rs-side-title {
          color: #bfdbfe;
        }

        .ns-rs-featured-card {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 14px;
          padding: 14px;
          cursor: pointer;
          transition: background 0.16s;
        }

        .ns-rs-featured-card:hover {
          background: rgba(255, 255, 255, 0.18);
        }

        .ns-rs-featured-card strong {
          display: block;
          color: #ffffff;
          font-size: 0.88rem;
          font-weight: 950;
          margin-bottom: 6px;
        }

        .ns-rs-featured-card p {
          color: #bfdbfe;
          font-size: 0.76rem;
          line-height: 1.5;
          margin: 0 0 10px;
        }

        .ns-rs-featured-action {
          color: #93c5fd;
          font-size: 0.75rem;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }

        .ns-rs-quick {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          padding: 16px;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
        }

        .ns-rs-quick-list {
          display: grid;
          gap: 8px;
        }

        .ns-rs-quick-item {
          width: 100%;
          border: 1px solid rgba(15, 23, 42, 0.07);
          background: #f8fafc;
          border-radius: 14px;
          padding: 10px;
          display: flex;
          align-items: center;
          gap: 9px;
          text-align: left;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .ns-rs-quick-item:hover {
          background: #eff6ff;
          border-color: #bfdbfe;
          transform: translateY(-1px);
        }

        .ns-rs-quick-icon {
          width: 32px;
          height: 32px;
          border-radius: 11px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .ns-rs-quick-item strong {
          display: block;
          color: #0f172a;
          font-size: 0.78rem;
          font-weight: 950;
          line-height: 1.2;
        }

        .ns-rs-quick-item span {
          display: block;
          color: #64748b;
          font-size: 0.69rem;
          margin-top: 2px;
        }

        .ns-rs-empty {
          min-height: 280px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1.5px dashed rgba(100, 116, 139, 0.28);
          border-radius: 20px;
          background: #ffffff;
        }

        @media (max-width: 1100px) {
          .ns-rs-layout {
            grid-template-columns: 1fr;
          }

          .ns-rs-side {
            position: static;
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .ns-report-hero {
            padding: 16px;
            border-radius: 22px;
          }

          .ns-report-title {
            font-size: 1.32rem;
          }

          .ns-report-search-wrap {
            min-width: 100%;
          }

          .ns-report-content-card {
            padding: 12px;
            border-radius: 22px;
          }

          .ns-report-search-help {
            flex-direction: column;
            align-items: flex-start;
          }

          .ns-rs-side {
            grid-template-columns: 1fr;
          }

          .ns-rs-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="content container-fluid ns-report-page">
        <section className="ns-report-hero">
          <div className="ns-report-hero-inner">
            <div>
              <div className="ns-report-eyebrow">Enterprise POS Reporting Console</div>

              <div className="ns-report-title-row">
                <span className="ns-report-title-icon">
                  <BarChart2 size={24} />
                </span>
                <h3 className="ns-report-title">Room Service & POS Reports</h3>
              </div>

              <p className="ns-report-subtitle">
                Centralized reporting for POS revenue, room service sales, item performance, tax collection, KOT status, and complimentary audit control. Designed for finance, outlet operations, and management review.
              </p>

              <div className="ns-report-hero-pills">
                <span className="ns-report-pill">
                  <FileText size={15} />
                  {totalAvailableReports} focused reports
                </span>
                <span className="ns-report-pill">
                  <Shield size={15} />
                  Tax & audit ready
                </span>
                <span className="ns-report-pill">
                  <Zap size={15} />
                  Fast report access
                </span>
                <span className="ns-report-pill">
                  <SearchIcon size={15} />
                  Search-ready catalogue
                </span>
              </div>
            </div>

            <div className="ns-report-search-wrap">
              <div className="ns-report-search-card">
                <Search
                  placeholder="Search sales, tax, item, KOT, complementary..."
                  allowClear
                  enterButton="Search"
                  value={searchQuery}
                  onChange={(event) => handleSearch(event.target.value)}
                  onSearch={handleSearch}
                />
                <div className="ns-report-search-help">
                  <span>Find reports by module, purpose, or route keyword.</span>
                  {q ? <strong>Filtering: {q}</strong> : <span>All reports visible</span>}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ns-report-content-card">
          <div className="ns-rs-layout">
            <main>
              <div className="ns-rs-section-head">
                <div>
                  <div className="ns-rs-kicker">POS Report Library</div>
                  <h4 className="ns-rs-heading">
                    Choose a report to analyze sales, tax, items, KOTs, and complementaries
                  </h4>
                  <p className="ns-rs-help">
                    {totalAvailableReports} reports across 3 categories. Use search to narrow by keyword: revenue, tax, item, KOT, complementary, or analytics.
                  </p>
                </div>

                <span className="ns-rs-result-pill">
                  <FileText size={14} />
                  {totalReports} report{totalReports === 1 ? "" : "s"}
                </span>
              </div>

              {filteredCategories.length === 0 ? (
                <div className="ns-rs-empty">
                  <Empty description="No reports matched. Try: sales, tax, item, KOT, or complementary." />
                </div>
              ) : (
                filteredCategories.map((category) => (
                  <section
                    key={category.category}
                    className="ns-rs-category-card"
                    style={{
                      background: `radial-gradient(circle at top left, ${category.bgAccent}, transparent 30%), #ffffff`,
                    }}
                  >
                    <div className="ns-rs-category-title-row">
                      <div>
                        <h4 className="ns-rs-category-title">
                          <span
                            className="ns-rs-category-dot"
                            style={{
                              background: category.dotColor,
                              boxShadow: `0 0 0 5px ${category.dotColor}22`,
                            }}
                          />
                          {category.category}
                        </h4>
                        <p className="ns-rs-category-description">
                          {category.description}
                        </p>
                      </div>

                      <span className="ns-rs-count">
                        {category.reports.length} available
                      </span>
                    </div>

                    <div className="ns-rs-grid">
                      {category.reports.map((report) => (
                        <div
                          key={report.title}
                          className="ns-rs-card"
                          onClick={() => handleCardClick(report.link)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleCardClick(report.link);
                            }
                          }}
                        >
                          <div className="ns-rs-card-inner">
                            <div>
                              <div className="ns-rs-card-top">
                                <span
                                  className="ns-rs-icon-box"
                                  style={{
                                    background: `${category.color}15`,
                                    color: category.color,
                                  }}
                                >
                                  {report.icon}
                                </span>

                                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                  <span
                                    className="ns-rs-badge"
                                    style={{
                                      background: `${category.color}10`,
                                      color: category.color,
                                      borderColor: `${category.color}30`,
                                    }}
                                  >
                                    {report.badge}
                                  </span>
                                  <span className="ns-rs-route-tag">{report.route}</span>
                                </div>
                              </div>

                              <div style={{ marginTop: 12 }}>
                                <h5 className="ns-rs-card-title">{report.title}</h5>
                                <p className="ns-rs-card-description">
                                  {report.description}
                                </p>
                              </div>
                            </div>

                            <div className="ns-rs-card-footer">
                              <span className="ns-rs-signal">
                                <Shield size={12} />
                                {report.signal}
                              </span>
                              <span className="ns-rs-open">
                                <ArrowRight size={14} />
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))
              )}
            </main>

            <aside className="ns-rs-side">
              <section className="ns-rs-featured">
                <h4 className="ns-rs-side-title">
                  <Star size={16} color="#fbbf24" />
                  Recommended Start
                </h4>

                <div
                  className="ns-rs-featured-card"
                  onClick={() => handleCardClick(featuredReport.link)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleCardClick(featuredReport.link);
                    }
                  }}
                >
                  <strong>{featuredReport.title}</strong>
                  <p>{featuredReport.description}</p>
                  <span className="ns-rs-featured-action">
                    Open report <ArrowRight size={13} />
                  </span>
                </div>
              </section>

              <section className="ns-rs-quick">
                <h4 className="ns-rs-side-title">
                  <Zap size={16} color="#2563eb" />
                  Quick Links
                </h4>

                <div className="ns-rs-quick-list">
                  {filteredQuickLinks.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No links matched" />
                  ) : (
                    filteredQuickLinks.map((link) => (
                      <button
                        type="button"
                        key={link.title}
                        onClick={() => handleCardClick(link.link)}
                        className="ns-rs-quick-item"
                      >
                        <span className="ns-rs-quick-icon">{link.icon}</span>
                        <span>
                          <strong>{link.title}</strong>
                          <span>{link.helper}</span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </section>

              <section className="ns-rs-quick">
                <h4 className="ns-rs-side-title">
                  <Clock size={16} color="#0f766e" />
                  Reporting Tips
                </h4>

                <div className="ns-rs-quick-list">
                  {reportingTips.map((tip) => (
                    <div
                      key={tip.title}
                      className="ns-rs-quick-item"
                      style={{ cursor: "default" }}
                    >
                      <span className="ns-rs-quick-icon">{tip.icon}</span>
                      <span>
                        <strong>{tip.title}</strong>
                        <span>{tip.body}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReportLandingPage;
